'use client';

import { CinemarkLink } from '@/components/cinemark-link/cinemark-link';
import { LoadingSpinnerComponent } from '@/components/loading-spinner/loading-spinner';
import { LocationComponent } from '@/components/location/location';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs/tabs';
import { AnalyticsPageCategories } from '@/enums/analytics-type';
import { useSharedState } from '@/hooks/state/shared-state';
import { useTheaterContext } from '@/hooks/state/theater';
import { dataTestTransformer } from '@/utils/data-test-helper';
import { PagedListOfTheater } from '@cinemark/content-api-client';
import { usePathname, useSearchParams } from 'next/navigation';
import { ReactNode, useEffect, useState } from 'react';

type TheatersTabRoute = {
  alternateRoute: string; // alternate route for the tab since both "/theatres" and "/theaters" should be supported
  id: string;
  route: string;
  tabTitle: string;
};

const theatersNavigationRoutes: TheatersTabRoute[] = [
  { alternateRoute: '/theaters/favorites', id: 'favorites', route: '/theatres/favorites', tabTitle: 'Favorites' },
  { alternateRoute: '/theaters?tab=nearby', id: 'nearby', route: '/theatres?tab=nearby', tabTitle: 'Nearby' },
];

type TheatersLayoutProps = {
  children: ReactNode;
};

const TheatersLayout = ({ children }: TheatersLayoutProps): React.JSX.Element => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [currentTab, setCurrentTab] = useState<TheatersTabRoute>();
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [initialTheaters, setInitialTheaters] = useState<PagedListOfTheater>();
  const { data: sharedState, isLoading: isLoadingSharedState } = useSharedState();
  const { data: theater, isLoading: isLoadingTheater } = useTheaterContext();
  const showLocationSelection = hasInitiallyLoaded && (pathname === '/theaters' || pathname === '/theatres')
    && !sharedState?.lastTextSearch && !sharedState?.lastGeoPosition && !initialTheaters && !theater;

  useEffect(() => {
    let pathnameToUse = pathname;
    const searchParamsToUse = searchParams.toString().toLowerCase();
    const fullPath = searchParamsToUse ? `${pathname}?${searchParamsToUse}` : pathname;
    const tabParam = searchParams.get('tab');

    if ((pathname === '/theatres' || pathname === '/theaters') && !tabParam) {
      pathnameToUse = `${pathname}?tab=nearby`;
    }

    const foundTab = theatersNavigationRoutes.find(r =>
      r.route === fullPath || r.alternateRoute === fullPath || r.route === pathnameToUse || r.alternateRoute === pathnameToUse);
    setCurrentTab(foundTab);
  }, [pathname, searchParams]);

  useEffect(() => {
    if (!isLoadingSharedState && !isLoadingTheater) {
      setHasInitiallyLoaded(true);
    }
  }, [isLoadingSharedState, isLoadingTheater]);

  const handleResults = (results: PagedListOfTheater): void => {
    setInitialTheaters(results);
  };

  if (!hasInitiallyLoaded) {
    return <LoadingSpinnerComponent center="within-page" />;
  }

  if (showLocationSelection) {
    return (
      <div className="container-lg">
        <LocationComponent className="text-center"
          title="Find Theatres Near You"
          onResults={handleResults}
        />
      </div>
    );
  }

  if (currentTab) {
    return (
      <Tabs className="container-xl"
        value={currentTab.id}
      >
        <div className="flex items-center justify-between flex-wrap gap-4 w-full">
          <TabsList className="self-end w-full max-md:hidden">
            {theatersNavigationRoutes.map(route =>
              <TabsTrigger key={route.id}
                asChild
                className="w-1/2 md:w-auto"
                value={route.id}
              >
                <CinemarkLink dataLinkCategory={AnalyticsPageCategories.THEATERS}
                  dataLinkName={route.tabTitle}
                  dataTest={`tab-trigger-${dataTestTransformer(route.tabTitle)}`}
                  href={route.route}
                >
                  {route.tabTitle}
                </CinemarkLink>
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {children}
      </Tabs>
    );
  }

  return <>{children}</>;
};

export default TheatersLayout;
